<template>
  <div class="top-box">
    <div class="comp-tabs">
      <button
        class="comp-tab comp-tab--reply"
        :class="replyButtonClass"
        @click="handleReplyClick"
      >
        <svg class="comp-tab-ic" viewBox="0 0 24 24">
          <path
            d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"
          />
        </svg>
        {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
      </button>

      <button
        v-if="isPrivateNoteEnabled"
        class="comp-tab comp-tab--note"
        :class="noteButtonClass"
        @click="handleNoteClick"
      >
        <svg class="comp-tab-ic" viewBox="0 0 24 24">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
          <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4Z" />
        </svg>
        {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
      </button>
    </div>
    <div class="action-wrap">
      <div v-if="isMessageLengthReachingThreshold" class="tabs-title">
        <span :class="charLengthClass">
          {{ characterLengthWarning }}
        </span>
      </div>
    </div>
    <woot-button
      v-if="popoutReplyBox"
      variant="clear"
      icon="dismiss"
      color-scheme="secondary"
      class-names="popout-button"
      @click="$emit('click')"
    />
    <woot-button
      v-else
      variant="clear"
      icon="resize-large"
      color-scheme="secondary"
      class-names="popout-button"
      @click="$emit('click')"
    />
  </div>
</template>

<script>
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import {
  hasPressedAltAndPKey,
  hasPressedAltAndLKey,
} from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
export default {
  name: 'ReplyTopPanel',
  mixins: [eventListenerMixins],
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    setReplyMode: {
      type: Function,
      default: () => {},
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
    isPrivateNoteEnabled: {
      type: Boolean,
      default: true,
    }
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'message-error' : 'message-length';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
  methods: {
    handleKeyEvents(e) {
      if (hasPressedAltAndPKey(e)) {
        this.handleNoteClick();
      }
      if (hasPressedAltAndLKey(e)) {
        this.handleReplyClick();
      }
    },
    handleReplyClick() {
      this.setReplyMode(REPLY_EDITOR_MODES.REPLY);
    },
    handleNoteClick() {
      this.setReplyMode(REPLY_EDITOR_MODES.NOTE);
    },
  },
};
</script>

<style lang="scss" scoped>
.top-box {
  display: flex;
  align-items: center;
  background: transparent;
  padding: var(--space-small) 0 0;
}

// Abas do composer (Responder / Nota) — igual ao .comp-tab do protótipo
.comp-tabs {
  display: flex;
  align-items: center;
  gap: var(--space-micro);
  margin-right: auto;
}

.comp-tab {
  display: inline-flex;
  align-items: center;
  gap: var(--space-smaller);
  padding: var(--space-smaller) var(--space-small);
  border-radius: var(--border-radius-normal);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-bold);
  color: var(--s-500);
  cursor: pointer;
  transition: color 0.15s ease-in-out, background 0.15s ease-in-out;

  .comp-tab-ic {
    width: 15px;
    height: 15px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &:hover {
    color: var(--s-800);
    background: var(--s-50);
  }
}

.comp-tab--reply.is-active {
  // token da marca: navy-cyan no claro, cyan legível no escuro (US-4328)
  color: var(--color-woot-darker);
}

.comp-tab--note.is-active {
  color: var(--y-700);
}

// Botão de expandir (popout) alinhado no canto direito
::v-deep .popout-button {
  margin-left: auto;
}

.action-wrap {
  display: flex;
  align-items: center;
  margin: 0 var(--space-normal);
  font-size: var(--font-size-mini);

  .message-error {
    color: var(--r-600);
  }
  .message-length {
    color: var(--s-600);
  }
}
</style>
